import { PlusOutlined } from '@ant-design/icons';
import { EQUIPMENT_OPERATIONAL_STATUSES, PERMISSIONS } from '@navicosa/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useState } from 'react';
import { api, errorText } from '../../api/client';
import type { Brand, Equipment, Location, ProductListItem, Supplier } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';

const OP_LABEL = {
  active: 'Sẵn sàng',
  maintenance: 'Bảo trì',
  inactive: 'Tạm ngưng',
  retired: 'Thanh lý',
} as const;
const OP_COLOR = {
  active: 'green',
  maintenance: 'orange',
  inactive: 'default',
  retired: 'red',
} as const;
const COND_LABEL: Record<string, string> = { good: 'Tốt', fair: 'Khá', needs_repair: 'Cần sửa' };

/** Xe thực tế (CR-04): xe Navicosa và xe đối tác. Lịch xe/khóa lịch sẽ dùng ở Sprint 4. */
export function EquipmentPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { can } = useAuth();
  const canManage = can(PERMISSIONS.EQUIPMENT_MANAGE);
  const canSuppliers = can(PERMISSIONS.SUPPLIERS_READ);
  const [filters, setFilters] = useState<{
    q?: string;
    ownership_type?: string;
    operational_status?: string;
    page: number;
  }>({ page: 1 });
  const [editing, setEditing] = useState<Equipment | 'new' | null>(null);
  const [form] = Form.useForm();
  const ownership = Form.useWatch('ownership_type', form);

  const list = useQuery({
    queryKey: ['equipment', filters],
    queryFn: () => api.page<Equipment>('/admin/equipment', { ...filters, limit: 20 }),
  });
  const products = useQuery({
    queryKey: ['products-pick'],
    queryFn: async () => (await api.page<ProductListItem>('/admin/products', { limit: 100 })).items,
  });
  const brands = useQuery({
    queryKey: ['brands'],
    queryFn: () => api.get<Brand[]>('/admin/brands'),
  });
  const locs = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.get<Location[]>('/admin/locations'),
  });
  const suppliers = useQuery({
    queryKey: ['suppliers-pick'],
    queryFn: async () => (await api.page<Supplier>('/admin/suppliers', { limit: 100 })).items,
    enabled: canSuppliers,
  });

  const open = (e: Equipment | 'new') => {
    form.resetFields();
    if (e !== 'new') form.setFieldsValue(e);
    else
      form.setFieldsValue({
        ownership_type: 'owned',
        operational_status: 'active',
        condition_status: 'good',
      });
    setEditing(e);
  };
  const submit = async () => {
    const v = await form.validateFields();
    const body = { ...v, supplier_id: v.ownership_type === 'partner' ? v.supplier_id : null };
    try {
      if (editing === 'new') await api.post('/admin/equipment', body);
      else if (editing) {
        const { product_id: _p, ...rest } = body;
        await api.patch(`/admin/equipment/${editing.id}`, rest);
      }
      message.success('Đã lưu');
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ['equipment'] });
    } catch (e) {
      message.error(errorText(e));
    }
  };

  return (
    <Card
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          Thiết bị thực tế
        </Typography.Title>
      }
      extra={
        canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => open('new')}>
            Thêm xe
          </Button>
        )
      }
    >
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Mã xe, serial, model"
          allowClear
          onSearch={(v) => setFilters((f) => ({ ...f, q: v.trim() || undefined, page: 1 }))}
          style={{ width: 260 }}
        />
        <Select
          allowClear
          placeholder="Sở hữu"
          style={{ width: 160 }}
          onChange={(v) => setFilters((f) => ({ ...f, ownership_type: v, page: 1 }))}
          options={[
            { value: 'owned', label: 'Xe Navicosa' },
            { value: 'partner', label: 'Xe đối tác' },
          ]}
        />
        <Select
          allowClear
          placeholder="Tình trạng"
          style={{ width: 160 }}
          onChange={(v) => setFilters((f) => ({ ...f, operational_status: v, page: 1 }))}
          options={EQUIPMENT_OPERATIONAL_STATUSES.map((s) => ({ value: s, label: OP_LABEL[s] }))}
        />
      </Space>
      <Table<Equipment>
        rowKey="id"
        loading={list.isLoading}
        dataSource={list.data?.items}
        pagination={{
          current: filters.page,
          pageSize: 20,
          total: list.data?.meta.total,
          onChange: (page) => setFilters((f) => ({ ...f, page })),
        }}
        columns={[
          { title: 'Mã xe', dataIndex: 'equipment_code' },
          { title: 'Sản phẩm', render: (_, r) => r.product.name },
          {
            title: 'Hãng / model',
            render: (_, r) => [r.brand?.name, r.model].filter(Boolean).join(' ') || '—',
          },
          { title: 'Serial', dataIndex: 'serial_number', render: (v) => v ?? '—' },
          { title: 'Năm SX', dataIndex: 'manufacture_year', render: (v) => v ?? '—' },
          {
            title: 'Sở hữu',
            render: (_, r) =>
              r.ownership_type === 'owned' ? (
                <Tag color="blue">Navicosa</Tag>
              ) : (
                <Tag color="purple">{r.supplier?.name ?? 'Đối tác'}</Tag>
              ),
          },
          { title: 'Vị trí', render: (_, r) => r.location?.name ?? '—' },
          {
            title: 'Chất lượng',
            render: (_, r) => COND_LABEL[r.condition_status] ?? r.condition_status,
          },
          {
            title: 'Tình trạng',
            render: (_, r) => (
              <Tag color={OP_COLOR[r.operational_status]}>{OP_LABEL[r.operational_status]}</Tag>
            ),
          },
          ...(canManage
            ? [
                {
                  title: '',
                  render: (_: unknown, r: Equipment) => (
                    <Space>
                      <Button size="small" onClick={() => open(r)}>
                        Sửa
                      </Button>
                      <Popconfirm
                        title="Xóa xe này?"
                        description="Chỉ xóa được khi chưa có lịch sử. Nếu đã dùng, hãy chuyển sang 'Thanh lý'."
                        onConfirm={async () => {
                          try {
                            await api.del(`/admin/equipment/${r.id}`);
                            void qc.invalidateQueries({ queryKey: ['equipment'] });
                          } catch (e) {
                            message.error(errorText(e));
                          }
                        }}
                      >
                        <Button size="small" danger>
                          Xóa
                        </Button>
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]
            : []),
        ]}
      />
      <Modal
        open={editing !== null}
        title={editing === 'new' ? 'Thêm xe' : 'Sửa xe'}
        onCancel={() => setEditing(null)}
        onOk={submit}
        okText="Lưu"
        width={640}
        forceRender
      >
        <Form form={form} layout="vertical">
          <Form.Item name="product_id" label="Sản phẩm" rules={[{ required: true }]}>
            <Select
              disabled={editing !== 'new'}
              showSearch
              optionFilterProp="label"
              options={(products.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
            />
          </Form.Item>
          <Space wrap>
            <Form.Item
              name="equipment_code"
              label="Mã xe"
              rules={[
                { required: true, pattern: /^[A-Za-z0-9._-]{2,100}$/, message: 'Chữ, số, . _ -' },
              ]}
            >
              <Input placeholder="NV-SL-001" />
            </Form.Item>
            <Form.Item name="ownership_type" label="Sở hữu">
              <Select
                style={{ width: 160 }}
                options={[
                  { value: 'owned', label: 'Xe Navicosa' },
                  { value: 'partner', label: 'Xe đối tác' },
                ]}
              />
            </Form.Item>
            {ownership === 'partner' && (
              <Form.Item name="supplier_id" label="Đối tác" rules={[{ required: true }]}>
                <Select
                  style={{ width: 220 }}
                  showSearch
                  optionFilterProp="label"
                  options={(suppliers.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
                />
              </Form.Item>
            )}
          </Space>
          <Space wrap>
            <Form.Item name="brand_id" label="Hãng">
              <Select
                allowClear
                style={{ width: 160 }}
                options={(brands.data ?? []).map((b) => ({ value: b.id, label: b.name }))}
              />
            </Form.Item>
            <Form.Item name="model" label="Model">
              <Input />
            </Form.Item>
            <Form.Item name="serial_number" label="Serial">
              <Input />
            </Form.Item>
            <Form.Item name="manufacture_year" label="Năm SX">
              <InputNumber min={1980} max={2100} />
            </Form.Item>
          </Space>
          <Space wrap>
            <Form.Item name="location_id" label="Vị trí hiện tại">
              <Select
                allowClear
                style={{ width: 200 }}
                options={(locs.data ?? []).map((l) => ({ value: l.id, label: l.name }))}
              />
            </Form.Item>
            <Form.Item name="condition_status" label="Chất lượng">
              <Select
                style={{ width: 140 }}
                options={Object.entries(COND_LABEL).map(([value, label]) => ({ value, label }))}
              />
            </Form.Item>
            <Form.Item name="operational_status" label="Tình trạng">
              <Select
                style={{ width: 140 }}
                options={EQUIPMENT_OPERATIONAL_STATUSES.map((s) => ({
                  value: s,
                  label: OP_LABEL[s],
                }))}
              />
            </Form.Item>
          </Space>
          <Form.Item name="internal_notes" label="Ghi chú nội bộ">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
